import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bulkAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import {
  Pill, Package, Activity, ClipboardList, Folders, Layers,
  Download, Upload, History, FileSpreadsheet, AlertTriangle, CheckCircle2,
  XCircle, Loader2, GitBranch, FileDown, BookOpen
} from 'lucide-react';

const MODULE_META = [
  { key: 'medications', label: 'Medicamentos', icon: Pill, color: 'bg-blue-50 text-blue-700 border-blue-200', desc: 'Catálogo central de medicamentos con dosis, vías y precauciones' },
  { key: 'presentations', label: 'Presentaciones', icon: Package, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', desc: 'Presentaciones farmacéuticas vinculadas a medicamentos por código' },
  { key: 'actions', label: 'Acciones Clínicas', icon: Activity, color: 'bg-amber-50 text-amber-700 border-amber-200', desc: 'Acciones disponibles durante las simulaciones' },
  { key: 'treatments', label: 'Tratamientos', icon: ClipboardList, color: 'bg-purple-50 text-purple-700 border-purple-200', desc: 'Tratamientos compuestos por medicamentos, acciones y cuidados' },
  { key: 'scenarios', label: 'Escenarios', icon: Folders, color: 'bg-rose-50 text-rose-700 border-rose-200', desc: 'Escenarios clínicos completos con vitales, eventos y evaluación' },
];

const TYPE_LABELS = { medications: 'Medicamentos', presentations: 'Presentaciones', actions: 'Acciones', treatments: 'Tratamientos', scenarios: 'Escenarios' };
const ESTADO_BADGE = {
  ok: { label: 'OK', cls: 'bg-green-100 text-green-800' },
  advertencia: { label: 'Advertencia', cls: 'bg-amber-100 text-amber-800' },
  duplicado: { label: 'Duplicado', cls: 'bg-blue-100 text-blue-800' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-800' },
};
const RESULT_BADGE = {
  creado: 'bg-green-100 text-green-800', actualizado: 'bg-blue-100 text-blue-800',
  omitido: 'bg-gray-100 text-gray-700', fallido: 'bg-red-100 text-red-800',
};

function ModuleCard({ meta, count, onTemplate, onImport, onExport, onHistory, downloading }) {
  const Icon = meta.icon;
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`bulk-card-${meta.key}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg border ${meta.color}`}><Icon size={22} /></div>
          <div>
            <CardTitle className="text-base">{meta.label}</CardTitle>
            {count !== undefined && <span className="text-xs text-slate-500">{count} registros</span>}
          </div>
        </div>
        <CardDescription className="text-xs mt-1">{meta.desc}</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-2 pt-2">
        <Button variant="outline" size="sm" className="justify-start gap-1 text-xs" disabled={downloading}
          onClick={() => onTemplate(meta.key, false)} data-testid={`template-btn-${meta.key}`}>
          <Download size={14} /> Plantilla
        </Button>
        <Button variant="outline" size="sm" className="justify-start gap-1 text-xs" disabled={downloading}
          onClick={() => onTemplate(meta.key, true)} data-testid={`template-examples-btn-${meta.key}`}>
          <FileSpreadsheet size={14} /> Con ejemplos
        </Button>
        <Button size="sm" className="justify-start gap-1 text-xs" onClick={() => onImport(meta.key)}
          data-testid={`import-btn-${meta.key}`}>
          <Upload size={14} /> Importar
        </Button>
        <Button variant="outline" size="sm" className="justify-start gap-1 text-xs" disabled={downloading}
          onClick={() => onExport(meta.key)} data-testid={`export-btn-${meta.key}`}>
          <FileDown size={14} /> Exportar
        </Button>
        <Button variant="ghost" size="sm" className="justify-start gap-1 text-xs col-span-2"
          onClick={() => onHistory(meta.key)} data-testid={`history-btn-${meta.key}`}>
          <History size={14} /> Historial de cargas
        </Button>
      </CardContent>
    </Card>
  );
}

function DependencyMap({ dependencies }) {
  if (!dependencies?.length) return <p className="text-sm text-slate-500">Sin dependencias entre elementos.</p>;
  const missing = dependencies.filter(d => !d.existe);
  const grouped = {};
  dependencies.forEach(d => {
    const key = `${d.origen_tipo}:${d.origen_codigo}`;
    if (!grouped[key]) grouped[key] = { ...d, targets: [] };
    grouped[key].targets.push(d);
  });
  return (
    <div className="space-y-2" data-testid="dependency-map">
      {missing.length > 0 && (
        <div className="p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
          <AlertTriangle size={14} className="inline mr-1" />
          {missing.length} dependencia(s) faltante(s)
        </div>
      )}
      <div className="max-h-64 overflow-y-auto space-y-1">
        {Object.values(grouped).map((g, i) => (
          <div key={i} className="text-xs border rounded p-2">
            <span className="font-semibold capitalize">{g.origen_tipo}</span>{' '}
            <span className="font-mono">{g.origen_codigo}</span>
            <div className="ml-4 mt-1 space-y-0.5">
              {g.targets.map((t, j) => (
                <div key={j} className={t.existe ? 'text-slate-600' : 'text-red-600 font-semibold'}>
                  ↳ {t.destino_tipo === 'medications' ? 'medicamento' : t.destino_tipo === 'presentations' ? 'presentación' : t.destino_tipo === 'actions' ? 'acción' : t.destino_tipo === 'treatments' ? 'tratamiento' : t.destino_tipo}{' '}
                  <span className="font-mono">{t.destino_codigo}</span> {t.existe ? '✓' : '✗ NO EXISTE'}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportWizard({ open, onClose, initialModule, onFinished }) {
  const [step, setStep] = useState('upload'); // upload | summary | preview | result
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [job, setJob] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [duplicateMode, setDuplicateMode] = useState('omitir');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setStep('upload'); setUploadResult(null); setJob(null);
    setSelectedTypes([]); setDuplicateMode('omitir'); setImportResult(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await bulkAPI.upload(file);
      setUploadResult(data);
      setStep('summary');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await bulkAPI.getJob(uploadResult.job_id);
      setJob(data);
      const detected = Object.keys(data.rows || {}).filter(k => (data.rows[k] || []).length > 0);
      setSelectedTypes(detected);
      setStep('preview');
    } catch (e) {
      toast.error('Error al cargar la previsualización');
    }
  };

  const runImport = async () => {
    if (!selectedTypes.length) { toast.error('Seleccione al menos un tipo de contenido'); return; }
    setImporting(true);
    try {
      const { data } = await bulkAPI.executeImport(job.id, selectedTypes, duplicateMode);
      setImportResult(data);
      setStep('result');
      onFinished?.();
      toast.success('Importación completada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la importación');
    } finally {
      setImporting(false);
    }
  };

  const detectedTypes = job ? Object.keys(job.rows || {}).filter(k => (job.rows[k] || []).length > 0) : [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard-dialog">
        <DialogHeader>
          <DialogTitle>Importación Masiva{initialModule ? ` — ${TYPE_LABELS[initialModule] || ''}` : ''}</DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Suba un archivo Excel (.xlsx) generado desde una plantilla de SimuClinic.'}
            {step === 'summary' && 'Contenido detectado en el archivo.'}
            {step === 'preview' && 'Revise el contenido, seleccione qué importar y cómo manejar duplicados.'}
            {step === 'result' && 'Resultado de la importación.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="border-2 border-dashed rounded-lg p-10 text-center space-y-3">
            <FileSpreadsheet size={40} className="mx-auto text-slate-400" />
            <input type="file" accept=".xlsx" ref={fileRef} className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} data-testid="bulk-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="select-file-btn">
              {uploading ? <><Loader2 size={16} className="animate-spin mr-2" /> Validando...</> : 'Seleccionar archivo .xlsx'}
            </Button>
            <p className="text-xs text-slate-500">Se aceptan plantillas individuales o la Plantilla Integral.</p>
          </div>
        )}

        {step === 'summary' && uploadResult && (
          <div className="space-y-4" data-testid="upload-summary">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(uploadResult.contenido_detectado || {}).map(([label, count]) => (
                <div key={label} className="border rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold">{count}</div>
                  <div className="text-xs text-slate-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Badge className={uploadResult.errores > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'} data-testid="summary-errors">
                Errores: {uploadResult.errores}
              </Badge>
              <Badge className="bg-amber-100 text-amber-800" data-testid="summary-warnings">
                Advertencias: {uploadResult.advertencias}
              </Badge>
              {Object.entries(uploadResult.duplicados || {}).map(([label, c]) => (
                <Badge key={label} className="bg-blue-100 text-blue-800">Duplicados {label}: {c}</Badge>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { reset(); }}>Subir otro archivo</Button>
              <Button onClick={loadPreview} data-testid="review-content-btn">Revisar contenido</Button>
            </div>
          </div>
        )}

        {step === 'preview' && job && (
          <div className="space-y-4">
            <Tabs defaultValue={detectedTypes[0]}>
              <TabsList className="flex-wrap h-auto">
                {detectedTypes.map(t => (
                  <TabsTrigger key={t} value={t} data-testid={`preview-tab-${t}`}>
                    {TYPE_LABELS[t]} ({(job.rows[t] || []).length})
                  </TabsTrigger>
                ))}
                <TabsTrigger value="_deps" data-testid="preview-tab-deps"><GitBranch size={14} className="mr-1" />Dependencias</TabsTrigger>
              </TabsList>
              {detectedTypes.map(t => (
                <TabsContent key={t} value={t}>
                  <div className="max-h-64 overflow-y-auto border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Estado</TableHead><TableHead>Código</TableHead>
                          <TableHead>Nombre</TableHead><TableHead>Dependencias</TableHead>
                          <TableHead>Errores / Advertencias</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(job.rows[t] || []).map((r, i) => (
                          <TableRow key={i}>
                            <TableCell><Badge className={ESTADO_BADGE[r.estado]?.cls}>{ESTADO_BADGE[r.estado]?.label}</Badge></TableCell>
                            <TableCell className="font-mono text-xs">{r.codigo}</TableCell>
                            <TableCell className="text-xs">{r.nombre}</TableCell>
                            <TableCell className="text-xs">
                              {(r.dependencias || []).map((d, j) => (
                                <span key={j} className={`inline-block mr-1 ${d.existe ? 'text-slate-500' : 'text-red-600 font-semibold'}`}>
                                  {d.codigo}{d.existe ? '' : ' ✗'}
                                </span>
                              ))}
                            </TableCell>
                            <TableCell className="text-xs">
                              {(r.errores || []).map((e, j) => <div key={j} className="text-red-600">{e}</div>)}
                              {(r.advertencias || []).map((w, j) => <div key={j} className="text-amber-600">{w}</div>)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              ))}
              <TabsContent value="_deps">
                <DependencyMap dependencies={job.dependencies} />
              </TabsContent>
            </Tabs>

            <div className="grid md:grid-cols-2 gap-4 border-t pt-4">
              <div>
                <Label className="font-semibold text-sm">Importación selectiva</Label>
                <div className="space-y-2 mt-2">
                  {detectedTypes.map(t => (
                    <div key={t} className="flex items-center gap-2">
                      <Checkbox id={`chk-${t}`} checked={selectedTypes.includes(t)}
                        onCheckedChange={(v) => setSelectedTypes(prev => v ? [...prev, t] : prev.filter(x => x !== t))}
                        data-testid={`select-type-${t}`} />
                      <Label htmlFor={`chk-${t}`} className="text-sm">{TYPE_LABELS[t]}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-semibold text-sm">Duplicados existentes</Label>
                <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="mt-2 space-y-1">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="omitir" id="dup-omitir" data-testid="dup-mode-omitir" />
                    <Label htmlFor="dup-omitir" className="text-sm">Omitir (por defecto)</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="actualizar" id="dup-actualizar" data-testid="dup-mode-actualizar" />
                    <Label htmlFor="dup-actualizar" className="text-sm">Actualizar existente</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="nueva_version" id="dup-version" data-testid="dup-mode-nueva-version" />
                    <Label htmlFor="dup-version" className="text-sm">Crear nueva versión</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep('summary')}>Volver</Button>
              <Button onClick={runImport} disabled={importing} data-testid="execute-import-btn">
                {importing ? <><Loader2 size={16} className="animate-spin mr-2" /> Importando...</> : 'Importar seleccionados'}
              </Button>
            </div>
          </div>
        )}

        {step === 'result' && importResult && (
          <div className="space-y-4" data-testid="import-result">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {['creados', 'actualizados', 'omitidos', 'fallidos'].map(k => (
                <div key={k} className="border rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${k === 'fallidos' && importResult.totales[k] > 0 ? 'text-red-600' : ''}`}>
                    {importResult.totales[k]}
                  </div>
                  <div className="text-xs text-slate-500 capitalize">{k}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1">
              {Object.entries(importResult.resultados || {}).map(([label, counts]) => (
                <div key={label} className="text-sm flex justify-between border-b py-1">
                  <span className="font-medium">{label}</span>
                  <span className="text-xs text-slate-600">
                    {counts.creados} creados · {counts.actualizados} actualizados · {counts.omitidos} omitidos · {counts.fallidos} fallidos
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => bulkAPI.downloadReport(importResult.job_id)} data-testid="download-report-btn">
                <Download size={14} className="mr-1" /> Descargar reporte
              </Button>
              <Button onClick={() => { reset(); onClose(); }} data-testid="close-wizard-btn">Finalizar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HistoryTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bulkAPI.getHistory({ page: 1, limit: 50 });
      setItems(data.items || []);
    } catch (e) {
      toast.error('Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div data-testid="bulk-history">
      {loading ? <Loader2 className="animate-spin mx-auto my-8" /> : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">Aún no hay cargas registradas.</p>
      ) : (
        <div className="border rounded overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead><TableHead>Usuario</TableHead><TableHead>Archivo</TableHead>
                <TableHead>Tipo</TableHead><TableHead>Estado</TableHead>
                <TableHead>Registros</TableHead><TableHead>Errores</TableHead><TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map(job => {
                const totalRows = Object.values(job.summary || {}).reduce((a, s) => a + (s?.total || 0), 0);
                return (
                  <TableRow key={job.id} data-testid={`history-row-${job.id}`}>
                    <TableCell className="text-xs">{new Date(job.created_at).toLocaleString('es-CL')}</TableCell>
                    <TableCell className="text-xs">{job.user_name}</TableCell>
                    <TableCell className="text-xs font-mono">{job.archivo}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{TYPE_LABELS[job.tipo] || job.tipo}</Badge></TableCell>
                    <TableCell>
                      <Badge className={job.status === 'importado' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-700'}>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{totalRows}</TableCell>
                    <TableCell className="text-xs text-red-600">{job.summary?.errores_totales || 0}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(job)} data-testid={`history-detail-${job.id}`}>Detalle</Button>
                        <Button variant="ghost" size="sm" onClick={() => bulkAPI.downloadOriginal(job.id)} title="Descargar archivo original"><FileDown size={14} /></Button>
                        <Button variant="ghost" size="sm" onClick={() => bulkAPI.downloadReport(job.id)} title="Descargar reporte"><Download size={14} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalle de carga</DialogTitle>
            <DialogDescription>{detail?.archivo} — {detail && new Date(detail.created_at).toLocaleString('es-CL')}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Usuario:</span> {detail.user_name}</div>
                <div><span className="text-slate-500">IP:</span> {detail.ip || '—'}</div>
                <div><span className="text-slate-500">Institución:</span> {detail.institucion || '—'}</div>
                <div><span className="text-slate-500">Modo duplicados:</span> {detail.duplicate_mode || '—'}</div>
              </div>
              <div className="border rounded p-2 space-y-1">
                {Object.entries(detail.summary || {}).filter(([k]) => TYPE_LABELS[k]).map(([k, s]) => (
                  <div key={k} className="flex justify-between text-xs">
                    <span className="font-medium">{TYPE_LABELS[k]}</span>
                    <span>{s.total} filas · {s.errores} errores · {s.advertencias} advertencias · {s.duplicados} duplicados</span>
                  </div>
                ))}
              </div>
              {detail.import_result && (
                <div className="border rounded p-2 space-y-1">
                  <div className="font-semibold text-xs mb-1">Resultado de importación</div>
                  {Object.entries(detail.import_result).map(([k, c]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span>{TYPE_LABELS[k]}</span>
                      <span>
                        <Badge className={`${RESULT_BADGE.creado} mr-1`}>{c.creados} creados</Badge>
                        <Badge className={`${RESULT_BADGE.actualizado} mr-1`}>{c.actualizados} act.</Badge>
                        <Badge className={`${RESULT_BADGE.omitido} mr-1`}>{c.omitidos} omit.</Badge>
                        <Badge className={RESULT_BADGE.fallido}>{c.fallidos} fall.</Badge>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LibrariesTab() {
  const [libs, setLibs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishOpen, setPublishOpen] = useState(false);
  const [pubName, setPubName] = useState('');
  const [pubDesc, setPubDesc] = useState('');
  const [pubPublic, setPubPublic] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bulkAPI.getLibraries();
      setLibs(data.items || []);
    } catch (e) { toast.error('Error al cargar bibliotecas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    if (!pubName.trim()) { toast.error('Ingrese un nombre para la biblioteca'); return; }
    setPublishing(true);
    try {
      const { data } = await bulkAPI.publishLibrary({
        nombre: pubName, descripcion: pubDesc,
        visibilidad: pubPublic ? 'publica' : 'restringida',
        instituciones_autorizadas: [],
      });
      toast.success(data.message);
      setPublishOpen(false); setPubName(''); setPubDesc('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al publicar la biblioteca');
    } finally { setPublishing(false); }
  };

  const doImport = async (lib) => {
    setImportingId(lib.id);
    try {
      const { data } = await bulkAPI.importLibrary(lib.id, 'omitir');
      setImportResult(data);
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar la biblioteca');
    } finally { setImportingId(null); }
  };

  const remove = async (lib) => {
    try {
      await bulkAPI.deleteLibrary(lib.id);
      toast.success('Biblioteca eliminada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div className="space-y-4" data-testid="libraries-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          Publique su biblioteca clínica (contenido con código) para que otras instituciones autorizadas la importen con un clic.
        </p>
        <Button onClick={() => setPublishOpen(true)} className="gap-2" data-testid="publish-library-btn">
          <BookOpen size={16} /> Publicar biblioteca
        </Button>
      </div>

      {loading ? <Loader2 className="animate-spin mx-auto my-8" /> : libs.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">No hay bibliotecas publicadas visibles para su institución.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {libs.map(lib => (
            <Card key={lib.id} data-testid={`library-card-${lib.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base">{lib.nombre}</CardTitle>
                  <div className="flex gap-1">
                    <Badge variant="outline" className="text-xs">
                      {lib.visibilidad === 'publica' ? 'Pública' : 'Restringida'}
                    </Badge>
                    {lib.es_propia && <Badge className="bg-indigo-100 text-indigo-800 text-xs">Propia</Badge>}
                  </div>
                </div>
                <CardDescription className="text-xs">
                  {lib.descripcion || 'Sin descripción'} — {lib.institucion || lib.publisher_name} · {new Date(lib.created_at).toLocaleDateString('es-CL')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1">
                  {Object.entries(lib.contenido || {}).map(([label, count]) => (
                    <Badge key={label} variant="secondary" className="text-xs">{count} {label}</Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => doImport(lib)} disabled={importingId === lib.id}
                    data-testid={`import-library-btn-${lib.id}`}>
                    {importingId === lib.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Download size={14} className="mr-1" />}
                    Importar
                  </Button>
                  {lib.es_propia && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(lib)}
                      data-testid={`delete-library-btn-${lib.id}`}>
                      Eliminar
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="publish-library-dialog">
          <DialogHeader>
            <DialogTitle>Publicar biblioteca clínica</DialogTitle>
            <DialogDescription>
              Se publicará un snapshot de todos los medicamentos, presentaciones, acciones, tratamientos y escenarios con código de su institución.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="lib-name">Nombre</Label>
              <Input id="lib-name" value={pubName} onChange={e => setPubName(e.target.value)}
                placeholder="Ej: Biblioteca ACLS Hospital Central" data-testid="library-name-input" />
            </div>
            <div>
              <Label htmlFor="lib-desc">Descripción</Label>
              <Input id="lib-desc" value={pubDesc} onChange={e => setPubDesc(e.target.value)}
                placeholder="Contenido y propósito de la biblioteca" data-testid="library-desc-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="lib-public" checked={pubPublic} onCheckedChange={setPubPublic} data-testid="library-public-checkbox" />
              <Label htmlFor="lib-public" className="text-sm">Visible para todas las instituciones</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPublishOpen(false)}>Cancelar</Button>
            <Button onClick={publish} disabled={publishing} data-testid="confirm-publish-btn">
              {publishing ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Publicar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!importResult} onOpenChange={(v) => !v && setImportResult(null)}>
        <DialogContent data-testid="library-import-result-dialog">
          <DialogHeader>
            <DialogTitle>Biblioteca importada</DialogTitle>
            <DialogDescription>{importResult?.biblioteca}</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="space-y-2">
              <div className="grid grid-cols-4 gap-2">
                {['creados', 'actualizados', 'omitidos', 'fallidos'].map(k => (
                  <div key={k} className="border rounded p-2 text-center">
                    <div className="text-xl font-bold">{importResult.totales[k]}</div>
                    <div className="text-xs text-slate-500 capitalize">{k}</div>
                  </div>
                ))}
              </div>
              {Object.entries(importResult.resultados || {}).map(([label, c]) => (
                <div key={label} className="flex justify-between text-xs border-b py-1">
                  <span className="font-medium">{label}</span>
                  <span>{c.creados} creados · {c.omitidos} omitidos · {c.fallidos} fallidos</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function BulkImportCenterPage() {
  const [searchParams] = useSearchParams();
  const [modules, setModules] = useState({});
  const [tab, setTab] = useState('centro');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardModule, setWizardModule] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadModules = useCallback(async () => {
    try {
      const { data } = await bulkAPI.getModules();
      const map = {};
      (data.modules || []).forEach(m => { map[m.key] = m; });
      setModules(map);
    } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  useEffect(() => {
    const mod = searchParams.get('module');
    const action = searchParams.get('action');
    if (mod && action === 'import') {
      setWizardModule(mod);
      setWizardOpen(true);
    }
  }, [searchParams]);

  const handleTemplate = async (key, withExamples) => {
    setDownloading(true);
    try { await bulkAPI.downloadTemplate(key, withExamples); }
    catch (e) { toast.error('Error al descargar la plantilla'); }
    finally { setDownloading(false); }
  };

  const handleExport = async (key) => {
    setDownloading(true);
    try { await bulkAPI.exportModule(key, {}); toast.success('Exportación descargada'); }
    catch (e) { toast.error('Error al exportar'); }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-6" data-testid="bulk-import-center">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="bulk-center-title">
            Centro de Carga Masiva
          </h1>
          <p className="text-sm text-slate-500">
            Descargar plantilla → Completar → Subir → Validar → Previsualizar → Importar → Reportar → Auditar → Exportar
          </p>
        </div>
        <Button onClick={() => { setWizardModule(null); setWizardOpen(true); }} className="gap-2" data-testid="bulk-import-main-btn">
          <Upload size={16} /> Importar archivo
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="centro" data-testid="tab-centro">Centro de Carga</TabsTrigger>
          <TabsTrigger value="bibliotecas" data-testid="tab-bibliotecas">Bibliotecas Compartidas</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="centro" className="space-y-4">
          <Card className="border-indigo-200 bg-indigo-50/50" data-testid="bulk-card-integral">
            <CardContent className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg border bg-indigo-100 text-indigo-700 border-indigo-200"><Layers size={22} /></div>
                <div>
                  <div className="font-semibold">Plantilla Integral</div>
                  <p className="text-xs text-slate-600">
                    Biblioteca clínica completa en un solo archivo: medicamentos, presentaciones, acciones, tratamientos y escenarios con todas sus relaciones.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" disabled={downloading} onClick={() => handleTemplate('integral', false)} data-testid="template-btn-integral">
                  <Download size={14} className="mr-1" /> Plantilla
                </Button>
                <Button variant="outline" size="sm" disabled={downloading} onClick={() => handleTemplate('integral', true)} data-testid="template-examples-btn-integral">
                  <FileSpreadsheet size={14} className="mr-1" /> Con ejemplos
                </Button>
                <Button size="sm" onClick={() => { setWizardModule(null); setWizardOpen(true); }} data-testid="import-btn-integral">
                  <Upload size={14} className="mr-1" /> Importar
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULE_META.map(meta => (
              <ModuleCard key={meta.key} meta={meta}
                count={modules[meta.key]?.total_registros}
                onTemplate={handleTemplate}
                onImport={(k) => { setWizardModule(k); setWizardOpen(true); }}
                onExport={handleExport}
                onHistory={() => setTab('historial')}
                downloading={downloading} />
            ))}
          </div>

          <Card>
            <CardContent className="py-4 text-xs text-slate-600 space-y-1">
              <div className="font-semibold text-sm text-slate-800 mb-1">Orden de importación recomendado</div>
              <p>1. Catálogos (vías y formas farmacéuticas) → 2. Medicamentos → 3. Presentaciones → 4. Acciones → 5. Tratamientos → 6. Escenarios.</p>
              <p>El sistema detecta dependencias automáticamente: si una presentación referencia un medicamento inexistente, se reportará el error antes de importar.</p>
              <p>Los duplicados nunca se sobrescriben automáticamente; por defecto se omiten.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bibliotecas">
          <LibrariesTab />
        </TabsContent>

        <TabsContent value="historial">
          <HistoryTab refreshKey={refreshKey} />
        </TabsContent>
      </Tabs>

      <ImportWizard open={wizardOpen} initialModule={wizardModule}
        onClose={() => setWizardOpen(false)}
        onFinished={() => { loadModules(); setRefreshKey(k => k + 1); }} />
    </div>
  );
}
